import { useToastState } from '../contexts/ToastContext';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles = {
  success: 'bg-green-50 border-green-500 text-green-900',
  error: 'bg-red-50 border-red-500 text-red-900',
  warning: 'bg-amber-50 border-amber-500 text-amber-900',
  info: 'bg-blue-50 border-blue-500 text-blue-900',
};

const iconColors = {
  success: 'text-green-600',
  error: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-blue-600',
};

export default function ToastContainer() {
  const { toasts, dismiss } = useToastState();

  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-md pointer-events-none"
      data-testid="toast-container"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence>
        {toasts.filter(t => !!t.message).map(t => {
          const Icon = icons[t.type] || Info;
          return (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ type: 'spring', stiffness: 260, damping: 24 }}
              className={`pointer-events-auto flex items-start gap-3 p-4 rounded-xl border-l-4 shadow-lg ${styles[t.type] || styles.info}`}
              data-testid={`toast-${t.type}`}
              role="alert"
            >
              <Icon size={20} className={`flex-shrink-0 mt-0.5 ${iconColors[t.type] || iconColors.info}`} />
              <div className="flex-1 min-w-0">
                {t.title && <div className="font-bold text-sm mb-0.5">{t.title}</div>}
                <div className="text-sm break-words whitespace-pre-line">{t.message}</div>
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className="flex-shrink-0 hover:bg-black/10 rounded p-0.5"
                aria-label="Kapat"
                data-testid={`toast-dismiss-${t.id}`}
              >
                <X size={16} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
